import React, { useEffect, useRef, useState } from 'react';

import { useAtomValue } from 'jotai';
import { resendOTP } from '@features/dcb-flow';
import { resetTimerActive } from '@entities/dcb';
import { dcbAtom, languageAtom } from '@shared/store/slice-atoms';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from '@shared/ui/timer/styles/Timer.module.css';
import {
  isOrangeMoroccoApiSource,
  isVariationV3Route,
  isVariationV5Route,
} from '@shared/utils/general';
import { isLY, isQA } from '@entities/country';
import Icons from '@shared/config/constants/Icons';
import { isAg26MarketingVariation } from '@shared/ui/unlock-vuz-banner';

const DEFAULT_TIMER_SECONDS = 30;
const QA_TIMER_SECONDS = 5 * 60;

const Timer = ({ pin, antifraudUniqueId = '', clickId = '' }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const deferredTimeoutsRef = useRef<number[]>([]);
  const hasAppliedQACooldownRef = useRef(false);

  const dcbState = useAtomValue(dcbAtom);
  const language = useAtomValue(languageAtom);

  const homeData = dcbState.homeData;
  const subscribeData = dcbState.subscribeData;
  const timerCount = dcbState.timerCount;
  const timerActive = dcbState.timerActive;
  const isV3Route = isVariationV3Route(dcbState.variationKey);
  const isV5Route = isVariationV5Route(dcbState.variationKey);

  const isLYCountry = isLY(homeData.country);
  const isQACountry = isQA(homeData.country);
  const isLgAg26 = isAg26MarketingVariation();
  const isOrangeMorocco =
    isOrangeMoroccoApiSource(homeData?.source) || isOrangeMoroccoApiSource(dcbState.source);

  const timerSeconds = isQACountry ? QA_TIMER_SECONDS : DEFAULT_TIMER_SECONDS;

  const [timer, setTimer] = useState(timerSeconds);
  const [isPinSentAlertActive, setPinSentAlertActive] = useState(false);

  const isActive = timer <= 0;

  useEffect(() => {
    if (isQACountry && !hasAppliedQACooldownRef.current) {
      hasAppliedQACooldownRef.current = true;
      setTimer(QA_TIMER_SECONDS);
    }
  }, [isQACountry]);

  const handleResendPin = () => {
    if (!isActive) {
      return;
    }

    if (timerCount <= 2) {
      let inputFields = {
        sessionId: subscribeData.sessionId,
        lang: language.locale,
        otp: pin,
        antiFraudUniqueId: antifraudUniqueId,
        clickId,
      };
      resendOTP(inputFields, history, isV3Route);

      if (isLgAg26) {
        resetTimerActive();
        setTimer(timerSeconds);
        setPinSentAlertActive(true);
      } else if (isV3Route) {
        resetTimerActive();
        setTimer(timerSeconds);
        setPinSentAlertActive(true);
      } else if (isQACountry) {
        resetTimerActive();
        setTimer(timerSeconds);
      } else {
        const tid = window.setTimeout(() => setTimer((current) => current - 1), 1000);
        deferredTimeoutsRef.current.push(tid);
      }
    }
  };

  useEffect(() => {
    return () => {
      deferredTimeoutsRef.current.forEach(clearTimeout);
      deferredTimeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isPinSentAlertActive) return;

    const id = window.setTimeout(() => setPinSentAlertActive(false), 5000);
    return () => clearTimeout(id);
  }, [isPinSentAlertActive]);

  useEffect(() => {
    if (isOrangeMorocco || timer <= 0) return;

    const id = window.setTimeout(() => setTimer((current) => current - 1), 1000);
    return () => clearTimeout(id);
  }, [isOrangeMorocco, timer]);

  const getClassName = () => {
    if (isLgAg26) {
      return `${isActive ? styles.get_new_code_active_v5 : styles.get_new_code_v5}`;
    }

    if (isV5Route) {
      return `${isActive ? styles.otp_button_active_v5 : styles.otp_button_v5}`;
    }

    if (isV3Route) {
      const isDisabled = `${isActive ? styles.otp_button_active : styles.otp_button_disabled}`;

      return classNames(styles.otp_button, isDisabled);
    }

    const isDisabled = `${isActive ? '' : 'disabled'}`;

    return `resend-pin-code ${isDisabled}`;
  };

  // if (!subscribeData.isResendOTP) {
  //   return null;
  // }

  if (isOrangeMorocco) {
    return null;
  }

  const getTime = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    const formattedSeconds = seconds > 9 ? `${seconds}` : `0${seconds}`;

    return ` ${minutes > 9 ? minutes : `0${minutes}`}:${formattedSeconds}`;
  };

  if (!timerActive && !isLgAg26) {
    return (
      <p className={isV5Route ? styles.pin_was_sent_v5 : 'resend-pin-code finished'}>
        {t('otp_was_resent')}
      </p>
    );
  }

  return (
    <>
      {isV3Route && isPinSentAlertActive ? (
        <div className={styles.pinSendAlert}>
          <img src={Icons.ic_step_done_blue} alt="pin sent alert icon" />
          <p className={styles.pinSentAlertText}>{t('otp_resent')}</p>
        </div>
      ) : null}
      {timerCount <= 2 ? (
        <p className={getClassName()} onClick={handleResendPin}>
          {t(
            isLgAg26
              ? 'lg_ag26_get_new_code'
              : isV3Route
              ? 'resend_code'
              : isLYCountry
              ? 'otp_resend_message_ly'
              : 'resend_otp'
          )}
          {`${isActive ? '' : getTime()}`}
        </p>
      ) : null}
      {isLgAg26 && isPinSentAlertActive ? (
        <div className={styles.ag26ResendSuccess} role="status">
          <p>{t('lg_ag26_code_sent')}</p>
        </div>
      ) : null}
    </>
  );
};

export default Timer;
